import React from "react";
import { GlobalPortalNavigationKey } from "../config/navigation";
import { GlobalPortalNavigation } from "./PortalNavigation";
import { PortalLink } from "./PortalLink";
import { ClinicianAssistantHeaderButton } from "./ClinicianAssistantTrigger";

interface ClinicianPortalLayoutProps {
  activeGlobalKey?: GlobalPortalNavigationKey;
  clinicianCode: string;
  therapistRole?: string;
  children: React.ReactNode;
}

export function ClinicianPortalLayout({
  activeGlobalKey,
  clinicianCode,
  therapistRole,
  children,
}: ClinicianPortalLayoutProps) {
  return (
    <div className="clinician-portal">
      <div className="cp-shell">
        <GlobalPortalNavigation
          activeKey={activeGlobalKey}
          clinicianCode={clinicianCode}
          therapistRole={therapistRole}
        />
        <main className="cp-main">{children}</main>
      </div>
    </div>
  );
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export function Breadcrumbs({
  items,
  clinicianCode,
}: {
  items: readonly BreadcrumbItem[];
  clinicianCode: string;
}) {
  return (
    <nav className="cp-breadcrumbs" aria-label="Breadcrumb">
      <ol>
        {items.map((item, index) => (
          <li key={`${item.label}-${index}`}>
            {item.path ? (
              <PortalLink path={item.path} clinicianCode={clinicianCode}>
                {item.label}
              </PortalLink>
            ) : (
              <span aria-current="page">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

interface PortalHeaderProps {
  title: string;
  clinicianName?: string;
  clinicianNameLoading?: boolean;
  breadcrumbs?: React.ReactNode;
  children?: React.ReactNode;
  density?: "default" | "compact";
}

export function PortalHeader({
  title,
  clinicianName,
  clinicianNameLoading = false,
  breadcrumbs,
  children,
  density = "default",
}: PortalHeaderProps) {
  return (
    <header className="cp-page-header" data-density={density}>
      <div className="cp-page-header__top">
        <div>
          {breadcrumbs}
          <h1>{title}</h1>
        </div>
        <div className="cp-page-header__actions">
          <ClinicianAssistantHeaderButton />
          {clinicianName ? (
            <p className="cp-clinician-name">{clinicianName}</p>
          ) : clinicianNameLoading ? (
            <span
              className="cp-clinician-name cp-clinician-name--placeholder"
              data-testid="clinician-name-placeholder"
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>
      {children}
    </header>
  );
}
